import React, { useEffect, useState, useRef } from "react";
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';

function FovOriginHelpModal({ isOpen, onClose }) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(0);
  const animationRef = useRef(null);
  const startTimeRef = useRef(null);

  // =====================================================
  // 1. Modal visibility
  // =====================================================
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setProgress(0);
      startTimeRef.current = null;
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // =====================================================
  // 2. Animation loop
  // =====================================================
  useEffect(() => {
    if (!isOpen) return;

    const animate = (timestamp) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      const elapsed = timestamp - startTimeRef.current;
      const cycleDuration = 4000;
      let t = (elapsed % cycleDuration) / cycleDuration;

      // Ping-pong
      t = t < 0.5 ? t * 2 : 2 - t * 2;

      // Ease
      t = t < 0.5
        ? 2 * t * t
        : 1 - Math.pow(-2 * t + 2, 2) / 2;

      setProgress(t);
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationRef.current);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isVisible) return null;

  // =====================================================
  // 3. Correct right-angle geometry
  // =====================================================

  // Hypotenuse endpoints
  const dX = 50, dY = 50;
  const sX = 250, sY = 180;

  // O and P lie on DS
  // const oT = 0.35;
  const pT = 0.55;
  // P (final origin)
  const pX = dX + pT * (sX - dX);
  const pY = dY + pT * (sY - dY);

  // Initial O
  // O starts to the right of P, same height
  const oX0 = sX;
  const oY0 = pY;

  // Animate O → P
  const oX = oX0 + progress * (pX - oX0);
  const oY = oY0; // y stays fixed
  const originLabelOpacity = progress;

  return (
    <div
      className={`help-modal-backdrop ${isOpen ? "open" : ""}`}
      onClick={handleBackdropClick}
    >
      <div className={`help-modal ${isOpen ? "open" : ""}`}>
        <button className="help-modal-close" onClick={onClose}>
          &times;
        </button>

        <h3 className="help-modal-title">FOV Center as Origin</h3>

        <div className="help-modal-content">
          <div className="help-modal-diagram">
            <svg viewBox="0 0 300 220" className="geometry-svg">

              {/* Axes */}
              {/* <line x1="40" y1="180" x2="40" y2="30" stroke="#94a3b8" />
              <text x="25" y="25" fill="#94a3b8">y</text>
              <line x1="40" y1="180" x2="280" y2="180" stroke="#94a3b8" />
              <text x="275" y="200" fill="#94a3b8">x</text> */}

              {/* Right angle at O */}
              {/* <path
                d={`M ${oX} ${oY - 10} L ${oX + 10} ${oY - 10} L ${oX + 10} ${oY}`}
                fill="none"
                stroke="#94a3b8"
              /> */}

              {/* OD and OS */}
              <line
                x1={oX}
                y1={oY}
                x2={dX}
                y2={dY}
                stroke="#3b82f6"
                strokeDasharray="4 3"
                opacity="0.5" />
              <line
                x1={oX}
                y1={oY}
                x2={sX}
                y2={sY}
                stroke="#3b82f6"
                strokeDasharray="4 3"
                opacity="0.5" />

              {/* Construction lines to complete right-angle triangle */}
              {/* Horizontal projection from D to vertical through O */}
              <line
                x1={dX}
                y1={dY}
                x2={oX}
                y2={dY}
                stroke="#3b82f6"
                strokeWidth="1.5"
                strokeDasharray="4 3"
                opacity="0.5"
              />

              {/* Vertical projection from O up to horizontal through D */}
              <line
                x1={oX}
                y1={oY}
                x2={oX}
                y2={dY}
                stroke="#3b82f6"
                strokeWidth="1.5"
                strokeDasharray="4 3"
                opacity="0.5"
              />

              {/* Horizontal projection from O to P */}
              <line
                x1={oX}
                y1={oY}
                x2={pX}
                y2={pY}
                stroke="#ffffff"
                strokeWidth="1.5"
                strokeDasharray="4 3"
                opacity="0.5"
              />

              {/* PD and PS (collinear) */}
              <line
                x1={pX}
                y1={pY}
                x2={dX}
                y2={dY}
                stroke="#f97316"
                strokeDasharray="4 3"
                opacity="0.5" />
              <line
                x1={pX}
                y1={pY}
                x2={sX}
                y2={sY}
                stroke="#ef4444"
                strokeDasharray="4 3"
                opacity="0.5" />

              {/* Points */}
              <circle cx={dX} cy={dY} r="5" fill="#5cead4" />
              <circle cx={sX} cy={sY} r="5" fill="#ef4444" />
              <circle cx={oX} cy={oY} r="5" fill="#ffffff" />
              <circle cx={pX} cy={pY} r="5" fill="#f97316" />

              {/* Labels */}
              <text x={dX - 15} y={dY - 8} fill="#5cead4">D</text>
              <text x={sX + 8} y={sY + 5} fill="#ef4444">S</text>
              <text x={oX + 6} y={oY + 15} fill="#5cead4">O</text>
              <text x={pX - 18} y={pY + 5} fill="#f97316">P</text>

              {/* <text
                x={pX + 8}
                y={pY + 5}
                fill="#94a3b8"
                fontSize="11"
                opacity={originLabelOpacity}
              >
                (origin)
              </text> */}

              {/* Vector labels */}
              {/* <text x={(pX + dX) / 2 - 30} y={(pY + dY) / 2 + 15} fill="#ffffff">PD</text>
              <text x={(pX + sX) / 2 - 30} y={(pY + sY) / 2 + 15} fill="#ffffff">PS</text> */}

            </svg>
          </div>

          <p className="help-modal-description">
            The origin <b>O</b> represents the nominal machine geometry, where the
            source–object distance <b>OS</b> and object–detector distance <b>OD </b>
            are orthogonal, before any positional offsets or shifts are applied. The field-of-view center <b>P</b> lies on the
            hypotenuse connecting the source <b>S</b> and detector <b>D</b>. When the origin
            is shifted from <b>O</b> to <b>P</b>, the vectors <b>PD</b> and <b>PS</b> remain
            collinear, and are related by{" "}
            <InlineMath math="\vec{PD} = - \vec{OP} + \vec{OD}" /> and{" "}
            <InlineMath math="\vec{PS} = - \vec{OP} + \vec{OS}" />.
          </p>
        </div>
      </div>
    </div>
  );
}

export default FovOriginHelpModal;
